import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import AnimatedJSILogo from '../common/AnimatedJSILogo';
import { tokens, usePrimaryColor } from '../../theme/tokens';

const LandingPage: React.FC = () => {
  const primaryColor = usePrimaryColor();
  const handleLogin = () => {
    // This will be handled by the authentication system
    window.location.href = '/login';
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: primaryColor || tokens.colors.connectedPurple,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: '80px',
        padding: 0,
        margin: 0,
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1000,
      }}
    >
      <Grid container sx={{ height: 'calc(100vh - 80px)', width: '100%', margin: 0 }}>
        {/* Left Half - JSI Logo with Animation */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            padding: 0,
            margin: 0,
          }}
        >
          <AnimatedJSILogo size={400} />
        </Grid>

        {/* Right Half - TrackNow Information */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            padding: 4,
            textAlign: 'center',
            margin: 0,
          }}
        >
          <Box sx={{ maxWidth: 500, width: '100%' }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '3rem', md: '4rem' },
                fontWeight: 900,
                color: tokens.colors.white,
                marginBottom: 3,
                fontFamily: 'Area Normal Black, Inter, sans-serif',
              }}
            >
              Track Now
            </Typography>

            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '1rem !important', md: '1.5rem !important' },
                fontWeight: 500,
                color: tokens.colors.white,
                marginBottom: 2,
                lineHeight: 1.4,
                fontFamily: 'Area Normal Black, Inter, sans-serif',
              }}
            >
              Streamline Your Filing Management
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                color: tokens.colors.offWhite,
                marginBottom: 4,
                lineHeight: 1.6,
                fontFamily: 'Area Normal Black, Inter, sans-serif',
              }}
            >
              TrackNow is your comprehensive solution for managing client filings,
              tracking deadlines, and ensuring compliance. Stay organized, meet deadlines,
              and maintain seamless communication with your team.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Box
                component="button"
                onClick={handleLogin}
                sx={{
                  borderRadius: '999px',
                  height: 48,
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: '0.3px',
                  textTransform: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingLeft: 3,
                  paddingRight: 2,
                  gap: 1.5,
                  minWidth: 'fit-content',
                  whiteSpace: 'nowrap',
                  backgroundColor: tokens.colors.white,
                  color: primaryColor || tokens.colors.connectedPurple,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'Area Normal Black, Inter, sans-serif',
                  '&:hover': {
                    backgroundColor: tokens.colors.offWhite,
                    paddingRight: 2.5,
                    '& .arrow-circle': {
                      transform: 'rotate(-45deg)',
                    },
                  },
                  '&:focus-visible': {
                    outline: `2px dashed ${tokens.colors.white}`,
                    outlineOffset: 4,
                  },
                }}
              >
                Login to Continue
                <Box
                  className="arrow-circle"
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: primaryColor || tokens.colors.connectedPurple,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.2s ease',
                  }}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    style={{ color: tokens.colors.white }}
                  >
                    <path
                      d="M2 6L10 6M10 6L7 3M10 6L7 9"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LandingPage;
